import { useState, useEffect } from "react";
import { visionURL } from "../../config/environment";
import Jsona from "jsona";
import JsonApiService from "../../services/jsonapiService";
import visionCallService from "../../services/visionCallService";

const dataFormatter = new Jsona();

const JsonApiServiceOptions = {
  baseUrl: `${visionURL}/content_repository/api`,
  credentials: "include"
};
const contentRepositoryItemsApiService = new JsonApiService(
  "items",
  JsonApiServiceOptions
);
const contentRepositorySectionItemsApiService = new JsonApiService(
  "section_items",
  JsonApiServiceOptions
);
const contentRepositoryItemTypesApiService = new JsonApiService(
  "item_types",
  JsonApiServiceOptions
);
const contentRepositoryItemCategoriesApiService = new JsonApiService(
  "categories",
  JsonApiServiceOptions
);

const excludedContentableTypes = type => {
  const excludedFromPolicy = ["Quiz", "Survey", "Homework", "SCORM"];
  const excludedFromCourse = ["Acknowledgement"];
  return type === "policy" ? excludedFromPolicy : excludedFromCourse;
};

export const useContentRepositoryItems = ({
  excludedCourseSectionId,
  courseOrPolicy = "course"
} = {}) => {
  const [isFetching, setIsFetching] = useState(false);
  const fetchContentRepositoryItems = async ({
    sortBy,
    page,
    filters,
    include = ["repositories", "trainingObjects"],
    fields = {
      repositoryItem: [
        "name",
        "archivedAt",
        "contentableType",
        "isGraceHill",
        "repository",
        "trainingObjects",
        "disabled"
      ]
    }
  }) => {
    setIsFetching(true);
    const response = await contentRepositoryItemsApiService
      .page(page)
      .sort(sortBy)
      .where({
        ...filters,
        courseSectionId: excludedCourseSectionId,
        excludedTypes: excludedContentableTypes(courseOrPolicy)
      })
      .include(include)
      .fields(fields)
      .all()
      .then(response => response.json());
    setIsFetching(false);
    return {
      data: dataFormatter.deserialize(response),
      totalCount: response.meta?.totalCount
    };
  };
  return {
    fetchContentRepositoryItems,
    isFetching
  };
};

export const useContentRepositorySectionItems = () => {
  const batchCreateSectionItems = async sectionItemsToCreate => {
    const operations = sectionItemsToCreate
      ? sectionItemsToCreate.map(sectionItem => {
          return visionCallService.serializeAtomicOperation(
            sectionItem,
            "sectionItem",
            "add",
            ["section", "item"]
          );
        })
      : [];
    const response = await contentRepositorySectionItemsApiService.operations(
      operations
    );
    return await response.json();
  };

  const createSectionItem = async ({ itemId, sectionId }) => {
    return await batchCreateSectionItems([
      {
        section: {
          id: sectionId,
          type: "section"
        },
        item: {
          id: itemId,
          type: "item"
        }
      }
    ]);
  };

  return {
    batchCreateSectionItems,
    createSectionItem
  };
};

export const useContentRepositoryItemTypes = (courseOrPolicy = "course") => {
  const [isFetching, setIsFetching] = useState(false);
  const [itemTypes, setItemTypes] = useState([]);
  const fetchContentRepositoryItemTypes = async () => {
    setIsFetching(true);
    const response = await contentRepositoryItemTypesApiService
      .where({ excludedTypes: excludedContentableTypes(courseOrPolicy) })
      .all()
      .then(response => response.json());
    setIsFetching(false);
    const categories = dataFormatter.deserialize(response);
    setItemTypes(categories);
    return categories;
  };

  useEffect(() => {
    fetchContentRepositoryItemTypes();
  }, []);

  return {
    itemTypes,
    isFetching
  };
};

export const useContentRepositoryItemCategories = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [itemCategories, setItemCategories] = useState([]);
  const fetchContentRepositoryItemCategories = async () => {
    setIsFetching(true);
    const response = await contentRepositoryItemCategoriesApiService
      .all()
      .then(response => response.json());
    setIsFetching(false);
    const categories = dataFormatter.deserialize(response);
    setItemCategories(categories);
    return categories;
  };

  useEffect(() => {
    fetchContentRepositoryItemCategories();
  }, []);

  return {
    itemCategories,
    isFetching
  };
};
